import { useDispatch } from "react-redux"
import { ISmtpSettings, SmtpSettingsBasic, SmtpSettingsConfigType, SmtpSettingsDefault, SmtpSettingsOfficeConfidential, SmtpSettingsOfficePublic } from "../../../core/store/typings/ISmtpSettings"
import { ChoiceGroup, DatePicker, DayOfWeek, getTheme, IChoiceGroupOption, Spinner, Stack, Text, TextField, Toggle } from "@fluentui/react"
import { Control, Controller, FieldErrors, SubmitHandler, useForm } from "react-hook-form"
import { useState } from "react"
import { strings } from "../../../localization/strings"
import { SecondaryButton } from "../../../components/shared/buttons/SecondaryButton"
import { onRenderLabel } from "../../../components/renders/onRenderLabel"
import { updateUTCDateTime } from "../../../core/helpers/date-time"
import { RemoveArea } from "../../../components/shared/decorations/RemoveArea"
import { CustomSeparator } from "../../../components/shared/decorations/CustomSeparator"
import { actionCreators } from "../../../core/actions/settings-actions";
import {
    getValidateEmailRule
} from "../../../components/shared/configurator/stages/assessment/dataCollection/modeConfigurator/pivots/helpers/validationRules";

export type TSettingsSmtpTabProps = {
    data?: ISmtpSettings
    isUpdating: boolean
}

const smtpSettingsConfigTypes: IChoiceGroupOption[] = [
    { key: SmtpSettingsConfigType.Default.toString(), text: "Default", iconProps: { iconName: "Mail" }, styles: { root: { display: 'flex' } } },
    { key: SmtpSettingsConfigType.Basic.toString(), text: "Custom", iconProps: { iconName: "MailOptions" }, styles: { root: { display: 'flex' } } },
    { key: SmtpSettingsConfigType.Office365Confidential.toString(), text: "Office365 (Conf)", iconProps: { iconName: "OfficeLogo" }, styles: { root: { display: 'flex' } } },
    { key: SmtpSettingsConfigType.Office365Public.toString(), text: "Office365 (Pub)" , iconProps: { iconName: "OfficeLogo" }, styles: { root: { display: 'flex' } } },
]

type SmtpSettingsFormData = {
    default?: SmtpSettingsDefault,
    basic?: SmtpSettingsBasic,
    office365Public: SmtpSettingsOfficePublic,
    office365Confidential: SmtpSettingsOfficeConfidential
}

export const SettingsSmtpTab: React.FC<TSettingsSmtpTabProps> = props => {

    const dispatch = useDispatch();
    const [smtpSettingsConfigType, setSmtpSettingsConfigType] = useState<SmtpSettingsConfigType>(props.data?.configurationType ?? SmtpSettingsConfigType.Default);    

    const { control, handleSubmit, formState: { errors } } = useForm<SmtpSettingsFormData>({ defaultValues: {
        default: smtpSettingsConfigType === SmtpSettingsConfigType.Default ? { configurationType: SmtpSettingsConfigType.Default} : undefined,
        basic: smtpSettingsConfigType === SmtpSettingsConfigType.Basic ? { ...props.data as SmtpSettingsBasic} : undefined,
        office365Public: smtpSettingsConfigType === SmtpSettingsConfigType.Office365Public ? { ...props.data as SmtpSettingsOfficePublic} : undefined,
        office365Confidential: smtpSettingsConfigType === SmtpSettingsConfigType.Office365Confidential ? { ...props.data as SmtpSettingsOfficeConfidential } : undefined
    } });

    const onSubmit: SubmitHandler<SmtpSettingsFormData> = data => {
        const settingsMap = {
            [SmtpSettingsConfigType.Default]: () => data.default,
            [SmtpSettingsConfigType.Basic]: () => data.basic,
            [SmtpSettingsConfigType.Office365Public]: () => data.office365Public,
            [SmtpSettingsConfigType.Office365Confidential]: () => data.office365Confidential
        };

        const formData = settingsMap[smtpSettingsConfigType]();
        if (formData)
            formData.configurationType = smtpSettingsConfigType;

        dispatch(actionCreators.updateOrganizationSmtpSettings(formData));
    };

    return (
         <Stack style={{ width: 460 }} tokens={{ padding: 24, childrenGap: 16 }}>
             <form onSubmit={handleSubmit(onSubmit)} style={{ display: "flex", flexDirection: "column", flexWrap: "nowrap", height: "100%", boxSizing: "border-box" }}>
                <Stack horizontal horizontalAlign="space-between">
                    <Stack verticalFill tokens={{ childrenGap: 16 }} >
                        <Stack tokens={{ childrenGap: 16 }} >
                            <Stack.Item>
                                <Text style={{ color: theme.palette.black, fontSize: 28, fontWeight: "bold", letterSpacing: "-.04em" }}>{strings.ORGANIZATION.SETTINGS.SMTP.TITLE}</Text>
                            </Stack.Item>
                            <Stack.Item>
                                <ChoiceGroup
                                    required
                                    selectedKey={smtpSettingsConfigType.toString()}
                                    onChange={(ev, opt) => opt && setSmtpSettingsConfigType(Number.parseInt(opt.key) as SmtpSettingsConfigType)}
                                    options={smtpSettingsConfigTypes}
                                    key={`smtp-options-${smtpSettingsConfigType}`}
                                />
                            </Stack.Item>
                        </Stack>
                        {smtpSettingsConfigType === SmtpSettingsConfigType.Basic && 
                            <BasicSmtpSettingsPanel 
                                control={control}
                                errors={errors}
                            />}
                        
                        {smtpSettingsConfigType === SmtpSettingsConfigType.Office365Public && 
                            <Office365PublicSmtpSettingsPanel 
                                control={control}  
                                errors={errors}
                            />}
                        
                        {smtpSettingsConfigType === SmtpSettingsConfigType.Office365Confidential && 
                            <Office365ConfidentialSmtpSettingsPanel 
                                control={control}
                                errors={errors}
                            />}

                        <Stack horizontal tokens={{ childrenGap: 8 }}>
                            <SecondaryButton disabled={props.isUpdating} onClick={handleSubmit(onSubmit)}>{strings.BUTTONS.TEXT.SAVE}</SecondaryButton>
                            {props.isUpdating && (
                                <Spinner />
                            )}
                        </Stack>
                        <CustomSeparator />
                        <RemoveArea
                            href="1174"
                            hrefText={strings.ORGANIZATION.SETTINGS.SMTP.HELP.HREF_TEXT}
                            text={strings.ORGANIZATION.SETTINGS.SMTP.HELP.TEXT}
                            title={strings.ORGANIZATION.SETTINGS.SMTP.HELP.TITLE} /> 
                    </Stack>
                </Stack>
             </form>
         </Stack>
    )
}

type TSmtpSettingsBasicProps = {
    control: Control<SmtpSettingsFormData>
    errors: FieldErrors<SmtpSettingsFormData>
}

const BasicSmtpSettingsPanel: React.FC<TSmtpSettingsBasicProps> = ({control, errors}) => {
    return (
        <>
            <Controller
                control={control}
                name={"basic.config.Server"}
                rules={{
                    required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.SERVER_IS_REQUIRED,
                }}
                render={({ field }) =>
                    <TextField
                        required
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        onRenderLabel={onRenderLabel}
                        label={strings.ORGANIZATION.SETTINGS.SMTP.BASIC.SERVER.NAME}
                        placeholder={strings.ORGANIZATION.SETTINGS.SMTP.BASIC.SERVER.NAME_PLACEHOLDER}
                        errorMessage={errors.basic?.config?.Server?.message}
                    />
                }
            /> 
            <Stack horizontal tokens={{ childrenGap: 8 }} style={{justifyContent:"space-between"}}>
                <Controller
                    control={control}
                    name={"basic.config.Port"}
                    rules={{
                        required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.PORT_IS_REQUIRED,
                        min: {
                            value: 0,
                            message: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.PORT_RANGE
                        },
                        max:{
                            value: 65535,
                            message: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.PORT_RANGE
                        },
                        validate: (value) => {
                            const port = value != undefined ? Number.parseInt(value.toString()) : undefined
                            if (!port){
                                return strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.PORT_IS_INVALID
                            }

                            return true
                        }
                    }}
                    render={({ field }) =>
                        <TextField
                            required
                            value={ field.value?.toString() }
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            onRenderLabel={onRenderLabel}
                            label={strings.ORGANIZATION.SETTINGS.SMTP.BASIC.PORT.NAME}
                            placeholder={strings.ORGANIZATION.SETTINGS.SMTP.BASIC.PORT.NAME_PLACEHOLDER}
                            errorMessage={errors.basic?.config?.Port?.message}
                        />
                    }
                />
                <Controller
                    control={control}
                    name={"basic.config.UseSsl"}
                    render={({ field }) =>
                        <Toggle
                            checked={!!(field.value)}
                            onChange={(e, ch) => field.onChange(ch)}
                            onBlur={field.onBlur}
                            label={strings.ORGANIZATION.SETTINGS.SMTP.BASIC.USE_SSL.NAME}
                            offText={strings.BUTTONS.TOGGLE_NO}
                            onText={strings.BUTTONS.TOGGLE_YES}
                        />
                    }
                />
            </Stack>

            <Controller
                control={control}
                name={"basic.config.Display"}
                rules={{
                    required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.DISPLAY_NAME_IS_REQUIRED,
                }}
                render={({ field }) =>
                    <TextField
                        required
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        onRenderLabel={onRenderLabel}
                        label={strings.ORGANIZATION.SETTINGS.SMTP.BASIC.DISPLAY_NAME.NAME}
                        placeholder={strings.ORGANIZATION.SETTINGS.SMTP.BASIC.DISPLAY_NAME.NAME_PLACEHOLDER}
                        errorMessage={errors.basic?.config?.Display?.message}
                    />
                }
            />
            <Controller
                control={control}
                name={"basic.config.From"}
                rules={{
                        ...getValidateEmailRule(),
                        required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.FROM_IS_REQUIRED,
                    }}
                render={({ field }) =>
                    <TextField
                        required
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        onRenderLabel={onRenderLabel}
                        label={strings.ORGANIZATION.SETTINGS.SMTP.FROM.NAME}
                        placeholder={strings.ORGANIZATION.SETTINGS.SMTP.FROM.NAME_PLACEHOLDER}
                        errorMessage={errors.basic?.config?.From?.message}
                    />
                }
            />            
            <Controller
                control={control}
                name={"basic.config.User"}
                rules={{
                    required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.USER_IS_REQUIRED,
                }}
                render={({ field }) =>
                    <TextField
                        required
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        onRenderLabel={onRenderLabel}
                        label={strings.ORGANIZATION.SETTINGS.SMTP.BASIC.USER.NAME}
                        placeholder={strings.ORGANIZATION.SETTINGS.SMTP.BASIC.USER.NAME_PLACEHOLDER}
                        errorMessage={errors.basic?.config?.User?.message}
                    />
                }
            />
            <Controller
                control={control}
                name={"basic.config.Password"}
                rules={{
                    required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.PASSWORD_IS_REQUIRED,
                }}
                render={({ field }) =>
                    <TextField
                        required
                        type="password"
                        canRevealPassword
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        onRenderLabel={onRenderLabel}
                        label={strings.ORGANIZATION.SETTINGS.SMTP.BASIC.PASSWORD.NAME}
                        placeholder={strings.ORGANIZATION.SETTINGS.SMTP.BASIC.PASSWORD.NAME_PLACEHOLDER}
                        errorMessage={errors.basic?.config?.Password?.message}
                    />
                }
            />
        </>
    )
}
type TSmtpSettingsOfficePublicProps = {
    control: Control<SmtpSettingsFormData>
    errors: FieldErrors<SmtpSettingsFormData>
}
const Office365PublicSmtpSettingsPanel: React.FC<TSmtpSettingsOfficePublicProps> = ({control, errors}) => {

    return (
        <>
            <Controller
                control={control}
                name={"office365Public.config.TenantId"}
                rules={{
                    required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.TENANT_ID_IS_REQUIRED,
                }}
                render={({ field }) =>
                    <TextField
                        required
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        onRenderLabel={onRenderLabel}
                        label={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.TENANT_ID.NAME}
                        placeholder={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.TENANT_ID.NAME_PLACEHOLDER}
                        errorMessage={errors.office365Public?.config?.TenantId?.message}
                    />
                }
            /> 
            <Controller
                control={control}
                name={"office365Public.config.ClientId"}
                rules={{
                    required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.CLIENT_ID_IS_REQUIRED,
                }}
                render={({ field }) =>
                    <TextField
                        required
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        onRenderLabel={onRenderLabel}
                        label={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.CLIENT_ID.NAME}
                        placeholder={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.CLIENT_ID.NAME_PLACEHOLDER}
                        errorMessage={errors.office365Public?.config?.ClientId?.message}
                    />
                }
            />
            <Controller
                control={control}
                name={"office365Public.config.RedirectUri"}
                rules={{
                    required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.REDIRECT_URI_IS_REQUIRED,
                }}
                render={({ field }) =>
                    <TextField
                        required
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        onRenderLabel={onRenderLabel}
                        label={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.REDIRECT_URI.NAME}
                        placeholder={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.REDIRECT_URI.NAME_PLACEHOLDER}
                        errorMessage={errors.office365Public?.config?.RedirectUri?.message}
                    />
                }
            />            
            <Controller
                control={control}
                name={"office365Public.config.AccountName"}
                rules={{
                    ...getValidateEmailRule(),
                    required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.FROM_IS_REQUIRED,
                }}
                render={({ field }) =>
                    <TextField
                        required
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        onRenderLabel={onRenderLabel}
                        label={strings.ORGANIZATION.SETTINGS.SMTP.FROM.NAME}
                        placeholder={strings.ORGANIZATION.SETTINGS.SMTP.FROM.NAME_PLACEHOLDER}
                        errorMessage={errors.office365Public?.config?.AccountName?.message}
                    />
                }
            />
                <Controller
                    control={control}
                    name={"office365Public.config.Token"}
                    rules={{
                        required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.TOKEN_IS_REQUIRED,
                    }}
                    render={({ field }) =>
                        <TextField
                            required
                            multiline
                            rows={5}
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            onRenderLabel={onRenderLabel}
                            label={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.TOKEN.NAME}
                            placeholder={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.TOKEN.NAME_PLACEHOLDER}
                            errorMessage={errors.office365Public?.config?.Token?.message}
                        />
                    }
                />
        </>
    )
}

type TSmtpSettingsOfficeConfidentialProps = {
    control: Control<SmtpSettingsFormData>,
    errors: FieldErrors<SmtpSettingsFormData>
}
const Office365ConfidentialSmtpSettingsPanel: React.FC<TSmtpSettingsOfficeConfidentialProps> = ({control, errors}) => {

    return (
        <>
            <Controller
                control={control}
                name={"office365Confidential.config.TenantId"}
                rules={{
                    required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.TENANT_ID_IS_REQUIRED,
                }}
                render={({ field }) =>
                    <TextField
                        required
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        onRenderLabel={onRenderLabel}
                        label={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.TENANT_ID.NAME}
                        placeholder={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.TENANT_ID.NAME_PLACEHOLDER}
                        errorMessage={errors.office365Confidential?.config?.TenantId?.message}
                    />
                }
            /> 
            <Controller
                control={control}
                name={"office365Confidential.config.ClientId"}
                rules={{
                    required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.CLIENT_ID_IS_REQUIRED,
                }}
                render={({ field }) =>
                    <TextField
                        required
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        onRenderLabel={onRenderLabel}
                        label={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.CLIENT_ID.NAME}
                        placeholder={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.CLIENT_ID.NAME_PLACEHOLDER}
                        errorMessage={errors.office365Confidential?.config?.ClientId?.message}
                    />
                }
            />
            <Controller
                control={control}
                name={"office365Confidential.config.Secret"}
                rules={{
                    required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.CLIENT_SECRET_IS_REQUIRED,
                }}
                render={({ field }) =>
                    <TextField
                        required
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        onRenderLabel={onRenderLabel}
                        label={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.CLIENT_SECRET.NAME}
                        placeholder={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.CLIENT_SECRET.NAME_PLACEHOLDER}
                        errorMessage={errors.office365Confidential?.config?.Secret?.message}
                    />
                }
            />
            <Controller
                control={control}
                name={"office365Confidential.config.ExpiresOn"}
                rules={{
                    required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.EXPIRATION_DATE_IS_REQUIRED,
                }}
                render={({ field }) => {
                    const dateValue = typeof field.value === 'string'
                        ? new Date(field.value)
                        : field.value;
                    
                    return ( <Stack.Item>
                            {onRenderLabel({
                                title: strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.EXPIRATION_DATE.DESCRIPTION,
                                label: strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.EXPIRATION_DATE.NAME,
                                required: true
                            })}
                            <DatePicker
                                isRequired
                                firstDayOfWeek={DayOfWeek.Monday}
                                value={dateValue}
                                onSelectDate={(date) => field.onChange(updateUTCDateTime(field.value, date))}
                                formatDate={(date?: Date) => date?.toLocaleDateString() ?? ''}
                                style={{width: 200}}
                                onBlur={field.onBlur}
                                placeholder={strings.ORGANIZATION.SETTINGS.SMTP.OFFICE365.EXPIRATION_DATE.NAME_PLACEHOLDER}
                                strings={strings.DAY_PICKER}
                            />
                        </Stack.Item>)
                    }
                }
            />
            <Controller
                control={control}
                name={"office365Confidential.config.AccountId"}
                rules={{
                    ...getValidateEmailRule(),
                    required: strings.ORGANIZATION.SETTINGS.SMTP.VALIDATION.FROM_IS_REQUIRED,
                }}
                render={({ field }) =>
                    <TextField
                        required
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        onRenderLabel={onRenderLabel}
                        label={strings.ORGANIZATION.SETTINGS.SMTP.FROM.NAME}
                        placeholder={strings.ORGANIZATION.SETTINGS.SMTP.FROM.NAME_PLACEHOLDER}
                        errorMessage={errors.office365Confidential?.config?.AccountId?.message}
                    />
                }
            />
        </>
    )
}
const theme = getTheme();